import React, { useMemo } from 'react'

import { Rect, touchableClick } from '@sketch-hq/sketch-web-renderer'

import * as Styled from './EntityOverlay.styled'
import { SFSymbolLabel } from '@sketch/components'

export type TouchableClickEvent<E extends HTMLElement> =
  | React.MouseEvent<E>
  | React.TouchEvent<E>

export interface FrameOverlayEventHandlersProps {
  onOverlayClick?: (e: TouchableClickEvent<HTMLDivElement>) => void
  onOverlayDoubleClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  onFrameHeaderMouseMove?: (e: React.MouseEvent<HTMLDivElement>) => void
  onFrameHeaderClick?: (e: TouchableClickEvent<HTMLDivElement>) => void
  onFrameClick?: (e: TouchableClickEvent<HTMLDivElement>) => void
  onCtaClick?: (e: TouchableClickEvent<HTMLButtonElement>) => void
}

export interface EntityOverlayProps extends FrameOverlayEventHandlersProps {
  bounds: Rect
  rootExtent?: Rect
  artboardUUID: string
  artboardName: string
  selected?: boolean
  /**
   * By default, hovering over the frame or its header will show a
   * hover state (orange rectangle around the frame content) added through CSS.
   * This behavior  can be disabled by this prop when we want to manage the hover state ourselves.
   */
  disableAutoHover?: boolean
  isComponent?: boolean
}

const formatArtboardBounds = (bounds: Rect, rootExtent?: Rect) => {
  /**
   * Given we are currently using 2 ways of positioning elements in the canvas
   * - Relative %
   * - Absolute
   *
   * It was decided that if "rootExtent" was passed, this would mean it's a relative to the parent calculation
   * if not it's the absolute position
   */
  if (!rootExtent) {
    return {
      x: `${bounds.x}px`,
      y: `${bounds.y}px`,
      width: `${bounds.width}px`,
      height: `${bounds.height}px`,
    }
  }

  const x = `${(bounds.x / rootExtent.width) * 100}%`
  const y = `${(bounds.y / rootExtent.height) * 100}%`
  const width = `${(bounds.width / rootExtent.width) * 100}%`
  const height = `${(bounds.height / rootExtent.height) * 100}%`

  return { x, y, width, height }
}

/**
 * This element takes care of:
 * - Rendering the artboard name, cta and artboard outline.
 * - Tracking the user events on the artboard and emit them when needed.
 */
export const EntityOverlay = ({
  ref,
  ...props
}: EntityOverlayProps & {
  ref?: React.Ref<HTMLDivElement>
}) => {
  const {
    artboardUUID,
    artboardName,
    bounds,
    selected,
    disableAutoHover,
    rootExtent,
    onCtaClick,
    onOverlayClick,
    onOverlayDoubleClick,
    onFrameHeaderClick,
    onFrameHeaderMouseMove,
    onFrameClick,
    isComponent,
  } = props

  const { width, height, x, y } = useMemo(
    () => formatArtboardBounds(bounds, rootExtent),
    [bounds, rootExtent]
  )
  const overlayClickEvents = useTouchableClickHandler(onOverlayClick)

  const ctaClickEvents = useTouchableClickHandler(onCtaClick)
  const headerClickEvents = useTouchableClickHandler(onFrameHeaderClick)

  const frameClickEvents = useTouchableClickHandler(onFrameClick)

  return (
    <Styled.FrameOverlayContainer
      aria-selected={selected}
      data-testid="artboard-overlay"
      data-artboard-uuid={artboardUUID}
      ref={ref}
      /* Event handlers */
      onDoubleClick={onOverlayDoubleClick}
      {...overlayClickEvents}
      // This "tabIndex" is commented on purpose
      // to prevent the canvas from translating when a tab event happens
      // check https://github.com/sketch-hq/Cloud/issues/18432 to see the behaviour
      //
      // tabIndex={0}
      $disableHover={disableAutoHover}
      $isComponent={isComponent}
      style={{
        left: x,
        top: y,
        width: width,
        height: height,
        cursor: onOverlayDoubleClick || onOverlayClick ? 'pointer' : 'inherit',
      }}
    >
      <Styled.ArtboardHeader
        {...headerClickEvents}
        onMouseMove={onFrameHeaderMouseMove}
        style={{
          cursor: onFrameHeaderClick ? 'pointer' : 'inherit',
        }}
      >
        <Styled.ArtboardCTA
          // This "tabIndex" was added purposefully
          // to prevent the canvas from translating when a tab event happens
          // check https://github.com/sketch-hq/Cloud/issues/18432 to see the behaviour
          //
          tabIndex={-1}
          {...ctaClickEvents}
          style={{
            cursor: onCtaClick ? 'pointer' : 'inherit',
          }}
        >
          <Styled.ArtboardName>
            <SFSymbolLabel text={artboardName} />
          </Styled.ArtboardName>
          <Styled.ArrowIcon />
        </Styled.ArtboardCTA>
      </Styled.ArtboardHeader>
      <Styled.FrameContentOverlay
        {...frameClickEvents}
        style={{
          cursor: onFrameClick ? 'pointer' : 'inherit',
        }}
      />
    </Styled.FrameOverlayContainer>
  )
}

/**
 * Create a touch and a mouse click handler calling the same final handler function.
 * This is to handle both touch and mouse events in a consistent way.
 */
function useTouchableClickHandler<T extends HTMLElement>(
  handler?: (e: TouchableClickEvent<T>) => void
) {
  return useMemo(
    () => (handler ? touchableClick(handler) : undefined),
    [handler]
  )
}

EntityOverlay.displayName = 'EntityOverlay'

export default EntityOverlay
